import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, Loader2, Syringe, Pill, CalendarClock, Check, FileText, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  vaccination: Syringe,
  medication: Pill,
  other: CalendarClock,
};

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function ExtractRecordModal({ open, petId, record, onClose, onApplied, onQuotaChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({}); // index -> bool
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  useEffect(() => {
    if (!open || !record) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setQuotaExceeded(false);
    setSuggestions([]);
    setSummary("");
    (async () => {
      try {
        const res = await fetch(`${API}/pets/${petId}/records/${record.record_id}/extract`, {
          method: "POST", credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 402) {
          setQuotaExceeded(true);
          setError(data.detail || "You've used your free AI scan this month.");
          return;
        }
        if (!res.ok) {
          setError(data.detail || "AI extraction failed. Please try again.");
          return;
        }
        setSummary(data.summary || "");
        setSuggestions(data.suggestions || []);
        const all = {};
        (data.suggestions || []).forEach((_, i) => { all[i] = true; });
        setSelected(all);
        // Refresh the parent's usage meter on successful scan.
        onQuotaChange?.();
      } catch {
        if (!cancelled) setError("Network error. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, petId, record]); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module-level const; setters are stable

  if (!open) return null;

  const toggle = (i) => setSelected(s => ({ ...s, [i]: !s[i] }));
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleApply = async () => {
    const picks = suggestions.filter((_, i) => selected[i]);
    if (picks.length === 0) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/pets/${petId}/records/${record.record_id}/apply-extracted`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suggestions: picks }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Could not save reminders.");
        return;
      }
      onApplied?.(data.created || 0);
      onClose();
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="extract-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in-up font-['Figtree']">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF8A65] to-[#FFB380] flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-extrabold text-slate-800 font-['Nunito']">Auto-detect reminders</h2>
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                <FileText size={11} /> {record?.filename}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="extract-close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {quotaExceeded ? (
            <div className="text-center py-4" data-testid="extract-quota-block">
              <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-[#FFB74D] to-[#FF8A65] flex items-center justify-center mx-auto mb-4 shadow-md">
                <Crown size={28} className="text-white" />
              </div>
              <h3 className="text-lg font-extrabold text-slate-800 font-['Nunito'] mb-1">You've used your free AI scan</h3>
              <p className="text-sm text-slate-500 mb-5 max-w-xs mx-auto">
                Free plan includes <b>1 AI scan per month</b>. Upgrade for <b>unlimited</b> scans, passport export, and Travel Mode.
              </p>
              <div className="bg-[#FFF5E6] border border-[#FFD7AE] rounded-2xl px-4 py-3 text-xs text-[#E65100] mb-5 inline-flex items-center gap-2">
                <Sparkles size={13} /> Resets on the 1st of each month.
              </div>
              <div className="flex flex-col sm:flex-row gap-2 max-w-sm mx-auto">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors"
                  data-testid="extract-quota-close"
                >
                  Maybe later
                </button>
                <button
                  onClick={() => { onClose(); navigate("/settings"); }}
                  className="flex-1 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors flex items-center justify-center gap-2"
                  data-testid="extract-upgrade-btn"
                >
                  <Crown size={14} /> Upgrade to Premium
                </button>
              </div>
            </div>
          ) : (
          <>
          {error && (
            <div data-testid="extract-error" className="text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="extract-loading">
              <Loader2 size={32} className="text-[#FF8A65] animate-spin mb-4" />
              <p className="text-sm font-semibold text-slate-700">Reading the document…</p>
              <p className="text-xs text-slate-500 mt-1">Gemini is scanning for vaccines, meds and follow-ups.</p>
            </div>
          ) : suggestions.length === 0 ? (
            !error && (
              <div className="py-10 text-center">
                <FileText size={28} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No scheduled items found in this document.</p>
                <p className="text-xs text-slate-400 mt-1">You can still keep the file in Vet Records.</p>
              </div>
            )
          ) : (
            <>
              {summary && <p className="text-xs text-slate-500 mb-4 italic">"{summary}"</p>}
              <div className="space-y-2" data-testid="extract-suggestions">
                {suggestions.map((s, i) => {
                  const Icon = ICONS[s.kind] || CalendarClock;
                  const isOn = !!selected[i];
                  return (
                    <button
                      key={`${s.kind || "sug"}-${s.title || ""}-${s.due_date || i}`}
                      data-testid={`extract-suggestion-${i}`}
                      onClick={() => toggle(i)}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${isOn ? "border-[#FF8A65] bg-[#FFF5E6]" : "border-slate-100 bg-white hover:border-slate-200"}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isOn ? "bg-white" : "bg-slate-50"}`}>
                        <Icon size={16} className="text-[#FF8A65]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-800 truncate">{s.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Due {formatDate(s.due_date)}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center ${isOn ? "bg-[#FF8A65] border-[#FF8A65]" : "border-slate-300"}`}>
                        {isOn && <Check size={12} className="text-white" strokeWidth={3} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        {!quotaExceeded && (
        <div className="px-6 pb-6 pt-3 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button
            data-testid="extract-apply-btn"
            onClick={handleApply}
            disabled={saving || selectedCount === 0}
            className="flex-1 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {selectedCount === 0
              ? "Select reminders"
              : `Add ${selectedCount} reminder${selectedCount !== 1 ? "s" : ""}`}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
